import React, {useMemo} from 'react';
import {Layout} from 'components/layout';
import type {TextFieldProps} from './types';
import {FieldLabel} from './FieldLabel';
import {NullFieldPlaceholder} from './NullFieldPlaceholder';
import {TextFieldWithChanges} from './TextFieldWithChanges';
import {MuiTextFieldStyled} from './muiStyled';

/**
 * TextField input component
 * If diff is set, TextFieldWithChanges is used to show changes
 */
export const TextField: React.FC<TextFieldProps> = ({
  focusableId,
  showWhenReadonlyAndEmpty = false,
  ...props
}) => {
  const {
    label,
    placeholder,
    name,
    type,
    required = false,
    readonly = false,
    value,
    diff,
    error = false,
    onFocus,
    onBlur,
    layout,
    ...restProps
  } = props;

  const inputProps = useMemo(() => {
    return {
      ...restProps.slotProps?.input,
      id: focusableId ?? name,
    };
  }, [focusableId, name, restProps.slotProps?.input]);

  //const className = error === 'warning' ? 'Ebba-warning' : undefined;

  return diff ? (
    <TextFieldWithChanges
      {...props}
      slotProps={{
        input: inputProps,
      }}
    />
  ) : (
    <Layout {...layout}>
      {label && (
        <FieldLabel
          label={label}
          required={required}
          error={error}
          htmlFor={inputProps.id}
        />
      )}
      {readonly && !value && !showWhenReadonlyAndEmpty ? (
        <NullFieldPlaceholder />
      ) : (
        <MuiTextFieldStyled
          aria-invalid={error === true}
          required={required === true}
          size="small"
          fullWidth
          id={name}
          name={name}
          placeholder={placeholder}
          value={(value ?? '') as string | number}
          readonly={readonly}
          type={type}
          error={error === true}
          color={error === 'warning' ? 'warning' : error ? 'error' : undefined}
          slotProps={{
            ...restProps.slotProps,
            input: inputProps,
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          {...restProps}
          spellCheck={false}
        />
      )}
    </Layout>
  );
};
